import {queryCollectionByIds} from '@/services/FirestoreService';
import type {SlimMarker} from '@/services/FilterService';

/**
 * Hydrates a list of slim marker items into full Firestore documents.
 *
 * Items whose full document cannot be found (deleted/missing) are dropped.
 *
 * @param firestoreCollection The Firestore collection name (e.g. 'beyondMaps-properties').
 * @param slimItems Slim items to hydrate. Each must have an `id` field.
 * @returns Full documents in the same order as `slimItems`.
 */
type FullDoc = Record<string, unknown> & {id: string};

export async function hydrateSlimMarkers<T extends SlimMarker>(
  firestoreCollection: string,
  slimItems: T[],
): Promise<(T & FullDoc)[]> {
  if (!slimItems.length) return [];

  const fullDocs: FullDoc[] = await queryCollectionByIds(firestoreCollection, slimItems.map((s) => s.id), true);
  const byId = new Map<string, FullDoc>(fullDocs.map((d) => [d.id, d]));

  const hydrated: (T & FullDoc)[] = [];
  for (const slim of slimItems) {
    const full = byId.get(slim.id);
    if (!full) continue;
    hydrated.push({...slim, ...full} as T & FullDoc);
  }
  return hydrated;
}

/** A slim comp item as assembled by the backend-filtering store: a comps-array stub plus client decorations. */
interface SlimCompItem {
  id: string;
  parent?: SlimMarker;
  markerData?: unknown;
}

/**
 * Hydrates slim comp items into full comps by hydrating their distinct parent property docs and
 * rebuilding each comp from the parent's `comps` array. Comp ids are comps-array-entry ids, not
 * document ids, so comps cannot be hydrated by their own id the way markers are.
 *
 * Items whose parent doc or comps-array entry cannot be found are dropped with a console.error.
 * Each surviving item keeps its client decorations (`markerData`, `group`) and gets its `parent`
 * replaced with the hydrated parent doc.
 */
export async function hydrateSlimComps<T extends SlimCompItem>(
  slimComps: T[],
): Promise<(T & Record<string, unknown> & {parent: SlimMarker & FullDoc})[]> {
  if (!slimComps.length) return [];

  const parentsById = new Map<string, SlimMarker>();
  for (const comp of slimComps) {
    if (comp.parent?.id) {
      parentsById.set(comp.parent.id, comp.parent);
    }
  }

  const hydratedParents = await hydrateSlimMarkers('beyondMaps-properties', [...parentsById.values()]);
  const hydratedParentById = new Map(hydratedParents.map((parent) => [parent.id, parent]));

  const hydrated: (T & Record<string, unknown> & {parent: SlimMarker & FullDoc})[] = [];
  for (const comp of slimComps) {
    const parent = comp.parent?.id ? hydratedParentById.get(comp.parent.id) : undefined;
    if (!parent) {
      console.error(`Dropping comp '${comp.id}' from export: parent property '${comp.parent?.id}' not found.`);
      continue;
    }
    const fullComp = (parent.comps as {id: string}[] | undefined)?.find((c) => c.id === comp.id);
    if (!fullComp) {
      console.error(`Dropping comp '${comp.id}' from export: not found on parent property '${parent.id}'.`);
      continue;
    }
    hydrated.push({...comp, ...fullComp, parent} as T & Record<string, unknown> & {parent: SlimMarker & FullDoc});
  }
  return hydrated;
}
